import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ResearchLayout from '../../components/ResearchLayout';
import Preloader from '../../components/Preloader';
import Modal from '../../components/Modal';
import { PatientMeasurementsDisplay } from '../../components/MeasurementDisplay';
import AIAssessmentViewer from '../../components/AIAssessmentViewer';
import api from '../../services/api';
import { getQuestions } from '../../lib/questionsCache';
import { Link } from 'react-router-dom';

// Case Details Card Component (similar to QualityOfAnswersReport)
const CaseDetailsCard = ({ caseId, compact = false, squeezed = false, showAiResponse = false }) => {
  const [caseDetails, setCaseDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCaseDetails = async () => {
      if (!caseId) return;

      try {
        setLoading(true);
        const response = await api.get(`/cases/${caseId}`);
        if (response.data && response.data.case) {
          const caseData = response.data.case;

          // Ensure aiResponseData is present - parse from aiResponse if needed
          if (!caseData.aiResponseData && caseData.aiResponse) {
            try {
              caseData.aiResponseData = JSON.parse(caseData.aiResponse);
            } catch (e) {
              console.warn('Failed to parse aiResponse:', e);
            }
          }

          setCaseDetails(caseData);
        }
      } catch (error) {
        console.error('Error fetching case details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCaseDetails();
  }, [caseId]);

  if (loading) {
    return (
      <div className="text-center py-2">
        <div className="spinner-border spinner-border-sm text-success" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!caseDetails) {
    return (
      <div className="text-center py-2">
        <i className="fas fa-exclamation-triangle text-warning fa-lg"></i>
        <p className="text-muted small mt-1">Unable to load case details</p>
      </div>
    );
  }

  // Calculate BMI
  const bmi = caseDetails.weight && caseDetails.height ?
    (caseDetails.weight / ((caseDetails.height / 100) ** 2)).toFixed(1) : 'N/A';

  const sectionMargin = squeezed ? 'mb-2' : 'mb-3';
  const headingMargin = squeezed ? 'mb-1' : 'mb-2';
  const rowGap = squeezed ? 'g-1' : 'g-2';
  const padding = squeezed ? 'p-1' : 'p-2';

  return (
    <div className="case-details-full">
      {/* Case Header */}
      <div className="mb-2 text-center">
        <h6 className="text-primary mb-1">
          <i className="fas fa-user-injured me-2"></i>
          Case {String(caseDetails.id || '').replace(/^0+/, '')}
        </h6>
        <div className="d-flex justify-content-center gap-2 mb-2">
          {caseDetails.score && (
            <div className={`badge fs-6 ${caseDetails.score >= 70 ? 'bg-success' : caseDetails.score >= 50 ? 'bg-warning' : 'bg-danger'}`}>
              Score: {caseDetails.score}/100
            </div>
          )}
          <div className="badge bg-info">
            {caseDetails.assessmentCompletionPercentage ? `${caseDetails.assessmentCompletionPercentage}%` : 'N/A'} Complete
          </div>
        </div>
      </div>

      {/* 2x4 Grid Layout */}
      <div className="row g-2">
        {/* Row 1, Column 1: Basic Info */}
        <div className="col-6">
          <div className="card h-100 border-success">
            <div className="card-header bg-success text-white text-center py-1">
              <small><i className="fas fa-id-card me-1"></i>Basic Info</small>
            </div>
            <div className="card-body p-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Age:</small>
                <div className="fw-bold small">{caseDetails.age ? `${caseDetails.age}y` : 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">Gender:</small>
                <div className="fw-bold small">{caseDetails.gender || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 1, Column 2: Physical Measurements */}
        <div className="col-6">
          <div className="card h-100 border-primary">
            <div className="card-header bg-primary text-white text-center py-1">
              <small><i className="fas fa-weight me-1"></i>Physical</small>
            </div>
            <div className="card-body p-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Weight:</small>
                <div className="fw-bold small">{caseDetails.weight ? `${caseDetails.weight} kg` : 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Height:</small>
                <div className="fw-bold small">{caseDetails.height ? `${caseDetails.height} cm` : 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">BMI:</small>
                <div className="fw-bold small">{bmi}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">Waist:</small>
                <div className="fw-bold small">{caseDetails.waist ? `${caseDetails.waist} cm` : 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2, Column 1: Vital Signs */}
        <div className="col-6">
          <div className="card h-100 border-danger">
            <div className="card-header bg-danger text-white text-center py-1">
              <small><i className="fas fa-heartbeat me-1"></i>Vitals</small>
            </div>
            <div className="card-body p-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Sys BP:</small>
                <div className="fw-bold small">{caseDetails.systolicBloodPressure || 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Dia BP:</small>
                <div className="fw-bold small">{caseDetails.diastolicBloodPressure || 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Heart Rate:</small>
                <div className="fw-bold small">{caseDetails.restingHeartRate ? (caseDetails.restingHeartRate / 10).toFixed(0) : 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">O2 Sat:</small>
                <div className="fw-bold small">{caseDetails.oxygenSaturationLevel || 'N/A'}%</div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2, Column 2: Lifestyle */}
        <div className="col-6">
          <div className="card h-100 border-info">
            <div className="card-header bg-info text-white text-center py-1">
              <small><i className="fas fa-running me-1"></i>Lifestyle</small>
            </div>
            <div className="card-body p-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Activity:</small>
                <div className="fw-bold small">{caseDetails.activeDailyLifestyleLevel || 'N/A'}/10</div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Stress:</small>
                <div className="fw-bold small">{caseDetails.stressFrequency || 'N/A'}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Sleep:</small>
                <div className="fw-bold small">{caseDetails.wellSleepLevel || 'N/A'}/10</div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">Mood:</small>
                <div className="fw-bold small">{caseDetails.mood || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Evaluation Results - Only show if protocol allows AI response */}
      {showAiResponse && caseDetails.aiResponseData && Object.keys(caseDetails.aiResponseData).length > 0 && (
        <div className="mt-3">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-warning text-dark text-center py-1">
              <small><i className="fas fa-robot me-1"></i>Evaluation Results</small>
            </div>
            <div className="card-body p-2">
              <AIAssessmentViewer data={caseDetails.aiResponseData} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ReviewCases = () => {
  const { user } = useAuth();
  
  // State for protocols
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // State for selected protocol
  const [selectedPendingProtocol, setSelectedPendingProtocol] = useState('');
  const [selectedCompletedProtocol, setSelectedCompletedProtocol] = useState('');
  const [selectedProtocolAiResponse, setSelectedProtocolAiResponse] = useState(false);
  
  // State for pending cases (assigned to doctor but not completed)
  const [pendingCases, setPendingCases] = useState([]);
  const [pendingCasesLoading, setPendingCasesLoading] = useState(false);
  const [pendingCurrentPage, setPendingCurrentPage] = useState(1);
  const pendingCasesPerPage = 50;
  
  // State for completed cases
  const [completedCases, setCompletedCases] = useState([]);
  const [completedCasesLoading, setCompletedCasesLoading] = useState(false);
  const [completedCurrentPage, setCompletedCurrentPage] = useState(1);
  const completedCasesPerPage = 50;

  // State for view answers modal
  const [showAnswersModal, setShowAnswersModal] = useState(false);
  const [selectedCaseForReview, setSelectedCaseForReview] = useState(null);
  const [caseQuestions, setCaseQuestions] = useState([]);
  const [caseAnswers, setCaseAnswers] = useState([]);
  const [caseAnswersLoading, setCaseAnswersLoading] = useState(false);

  // State for access denial modal
  const [showAccessDenialModal, setShowAccessDenialModal] = useState(false);
  const [accessDenialTitle, setAccessDenialTitle] = useState('');
  const [accessDenialMessage, setAccessDenialMessage] = useState('');

  useEffect(() => {
    if (user) {
      loadProtocols();
    }
  }, [user]);

  const loadProtocols = async () => {
    try {
      // Get all assignments for this doctor to find protocols
      const response = await api.get(`/assignments?doctor_id=${user.id}`);
      const assignments = response.data.assignments || [];
      
      // Get unique protocol IDs
      const protocolIds = [...new Set(assignments.map(a => a.protocol_id).filter(Boolean))];
      
      // Fetch protocol details
      const protocolsResponse = await api.get('/protocols');
      const allProtocols = protocolsResponse.data.protocols || [];
      
      // Filter to only show protocols assigned to this doctor
      const doctorProtocols = allProtocols.filter(p => protocolIds.includes(p.id));
      
      // Add case count information
      const enhancedProtocols = doctorProtocols.map(protocol => {
        const protocolAssignments = assignments.filter(a => a.protocol_id === protocol.id);
        const pendingCount = protocolAssignments.filter(a => a.status !== 'completed').length;
        const completedCount = protocolAssignments.filter(a => a.status === 'completed').length;
        
        return {
          ...protocol,
          totalCases: protocolAssignments.length,
          pendingCount,
          completedCount,
          showAiResponse: protocol.show_ai_response || protocol.showAiResponse || false
        };
      });
      
      setProtocols(enhancedProtocols);
      setLoading(false);
    } catch (error) {
      console.error('Error loading protocols:', error);
      setProtocols([]);
      setLoading(false);
    }
  };

  // Load pending cases for selected protocol
  useEffect(() => {
    setPendingCurrentPage(1); // Reset to page 1 when protocol changes
    if (selectedPendingProtocol) {
      loadPendingCases(selectedPendingProtocol);
    } else {
      setPendingCases([]);
    }
  }, [selectedPendingProtocol]);

  // Helper: Validate that case can be accessed (batch not expired)
  const validateCaseAccess = async (batchId, caseId) => {
    try {
      console.log(`[ReviewCases] Validating access for case ${caseId} in batch ${batchId}...`);
      const response = await api.get(`/batches/${batchId}/case/${caseId}/access-check`);
      console.log(`[ReviewCases] Access validation result:`, response.data);
      
      if (response.data.access_granted) {
        // Access granted - safe to navigate
        return { allowed: true };
      }
    } catch (error) {
      console.error(`[ReviewCases] Access validation failed:`, error);
      
      // Check if error is due to batch expiration
      if (error.response?.status === 403) {
        const errorData = error.response.data;
        if (errorData.error === 'Batch expired') {
          console.error(`[ReviewCases]   BLOCKED: Batch is expired - cannot access this case`);
          return { 
            allowed: false, 
            reason: 'batch_expired',
            message: errorData.message,
            batch_name: errorData.batch_name
          };
        }
      }
      
      // Other errors - deny access
      console.error(`[ReviewCases] Access denied:`, error.response?.data?.message || error.message);
      return { 
        allowed: false, 
        reason: 'access_denied',
        message: error.response?.data?.message || 'Unable to verify access'
      };
    }
  };

  // Helper: Handle case navigation with validation
  const handleCaseNavigation = async (caseId, protocolId, batchId) => {
    console.log(`[ReviewCases] Navigation requested for case ${caseId}, batch ${batchId}`);
    
    // Validate access first
    const validation = await validateCaseAccess(batchId, caseId);
    
    if (!validation.allowed) {
      // Access denied
      console.error(`[ReviewCases] ❌ Navigation BLOCKED: ${validation.reason}`);
      
      if (validation.reason === 'batch_expired') {
        setAccessDenialTitle('  Cannot Access Case');
        setAccessDenialMessage(`Batch "${validation.batch_name}" has expired.\n\n${validation.message}\n\nYou can no longer review cases in this batch.`);
      } else {
        setAccessDenialTitle('❌ Access Denied');
        setAccessDenialMessage(`Batch Case Expired\n\nCase ID: ${caseId}\n\nReason:\n${validation.message}\n\nIf you believe this is an error, please contact your administrator.`);
      }
      setShowAccessDenialModal(true);
      return;
    }
    
    // Access granted - navigate
    console.log(`[ReviewCases] ✅ Access validated - navigating to answer questions page`);
    // Pass batch ID if available for better navigation experience after case completion
    const batchParam = batchId ? `&batch=${encodeURIComponent(batchId)}` : '';
    window.location.href = `/doctor/answer-questions/start?case=${caseId}&protocol=${protocolId}${batchParam}`;
  };

  // Helper: Get batch expiration status with fallback logic
  const getBatchExpirationStatus = async (batchId, batchName) => {
    try {
      // Try the lightweight expiration-status endpoint first
      const statusResponse = await api.get(`/batches/${batchId}/expiration-status`);
      return {
        is_expired: statusResponse.data.is_expired,
        effective_end_date: statusResponse.data.effective_end_date,
        batch_name: statusResponse.data.batch_name,
        source: 'expiration-status'
      };
    } catch (error) {
      console.warn(`[ReviewCases] Fallback: Could not get expiration status for batch ${batchId}:`, error.message);
      // Fallback: return default (not expired) so we don't lock users out
      return {
        is_expired: false,
        effective_end_date: null,
        batch_name: batchName,
        source: 'fallback'
      };
    }
  };

  const loadPendingCases = async (protocolId) => {
    setPendingCasesLoading(true);
    try {
      // Get both direct assignments AND batch assignments for this protocol
      const response = await api.get(
        `/assignments?doctor_id=${user.id}&protocol_id=${protocolId}&status=active,assigned_to_doctor`
      );
      let assignments = response.data.assignments || [];
      console.log('[ReviewCases] Loaded direct assignments:', assignments.length);
      
      // Also get batch assignments for this protocol
      try {
        console.log('[ReviewCases] [TEMPORARILY DISABLED] Batch loading is disabled for testing');
        /* TEMPORARILY DISABLED - Comment out batch loading
        
        // Fetch active batches (not yet expired)
        const activeBatchesResponse = await api.get('/batches/my-batches?status=active');
        const activeBatches = activeBatchesResponse.data.batches || [];
        console.log('[ReviewCases] Loaded active batches:', activeBatches.length, activeBatches);
        
        // Also fetch from assignment endpoint to get ALL batches doctor is assigned to
        // This is a fallback to catch any batches that might be in various states
        let allAssignedBatches = activeBatches;
        
        // For each batch, get the cases for this protocol
        for (const batch of allAssignedBatches) {
          try {
            console.log(`[ReviewCases] Loading cases for batch ${batch.id} (${batch.name}) and protocol ${protocolId}...`);
            const batchCasesResponse = await api.get(
              `/batches/${batch.id}/protocol/${protocolId}/cases`
            );
            const batchCases = batchCasesResponse.data.cases || [];
            
            // Get batch expiration status with fallback
            let batchIsExpired = batchCasesResponse.data.batch_is_expired;
            let batchEffectiveEndDate = batchCasesResponse.data.batch_effective_end_date;
            
            // If not in response, fetch separately
            if (batchIsExpired === undefined || batchIsExpired === null) {
              console.log(`[ReviewCases] batch_is_expired not in response for batch ${batch.id}, fetching separately...`);
              const expirationStatus = await getBatchExpirationStatus(batch.id, batch.name);
              batchIsExpired = expirationStatus.is_expired;
              batchEffectiveEndDate = expirationStatus.effective_end_date;
              console.log(`[ReviewCases] Got expiration status from ${expirationStatus.source}: is_expired=${batchIsExpired}`);
            }
            
            console.log(`[ReviewCases] Batch ${batch.id} (${batch.name}): ${batchCases.length} cases, is_expired=${batchIsExpired}`);
            console.log(`[ReviewCases] Response data:`, {
              batch_is_expired: batchIsExpired,
              batch_effective_end_date: batchEffectiveEndDate,
              batch_name: batchCasesResponse.data.batch_name,
              cases_count: batchCases.length
            });
            
            // Batch cases already have batch info embedded, use them directly
            const batchAssignments = batchCases.map(batchCase => ({
              id: `batch_${batch.id}_case_${batchCase.case_id}`,
              doctor_id: user.id,
              protocol_id: parseInt(protocolId),
              case_id: batchCase.case_id,
              batch_id: batchCase.batch_id || batch.id,
              batch_name: batchCasesResponse.data.batch_name || batch.name,
              batch_is_expired: batchIsExpired,
              batch_effective_end_date: batchEffectiveEndDate,
              status: 'active',
              assigned_at: batchCase.assigned_at || batch.created_at || new Date().toISOString(),
              is_batch: true,
              case: batchCase,
              caseData: batchCase
            }));
            
            assignments = assignments.concat(batchAssignments);
            console.log(`[ReviewCases] Added ${batchAssignments.length} cases from batch, all marked with batch_is_expired=${batchIsExpired}`);
          } catch (batchError) {
            console.error(`[ReviewCases] ERROR loading cases for batch ${batch.id}:`, batchError.message);
            console.error(`[ReviewCases] Error details:`, batchError.response?.data || batchError);
            console.error(`[ReviewCases] Status code:`, batchError.response?.status);
          }
        }
        
        END TEMPORARILY DISABLED */
      } catch (batchesError) {
        console.error('[ReviewCases] ERROR loading batches:', batchesError.message);
        console.error('[ReviewCases] Error details:', batchesError.response?.data || batchesError);
        console.error('[ReviewCases] Status code:', batchesError.response?.status);
        // Continue without batch cases rather than failing completely
      }
      
      console.log('[ReviewCases] Total assignments (direct + batch):', assignments.length);
      
      // DEDUPLICATE: Remove duplicate cases (same case_id + protocol_id)
      const seenCases = new Set();
      const uniqueAssignments = [];
      for (const assignment of assignments) {
        const key = `${assignment.case_id}_${assignment.protocol_id}`;
        if (!seenCases.has(key)) {
          seenCases.add(key);
          uniqueAssignments.push(assignment);
        } else {
          console.log(`[ReviewCases] Skipping duplicate: case ${assignment.case_id}, protocol ${assignment.protocol_id}`);
        }
      }
      console.log(`[ReviewCases] After deduplication: ${assignments.length} → ${uniqueAssignments.length} cases`);
      
      // Enhance with answer checking (case data already loaded via assignment.case from backend)
      const enhancedCases = await Promise.all(
        uniqueAssignments.map(async (assignment) => {
          try {
            console.log(`[ReviewCases] Processing assignment ID ${assignment.id}, case ${assignment.case_id}, protocol ${assignment.protocol_id}`);
            
            // OPTIMIZATION: Use case data already embedded in assignment object
            // This was loaded via backend JOIN, avoiding N+1 queries
            const caseData = assignment.case || { id: assignment.case_id, customerId: assignment.case_customer_id };
            
            // Check if there are already answers for this case
            let hasStarted = false;
            let answerCount = 0;
            try {
              const answersUrl = `/answers?protocol_id=${assignment.protocol_id}&case_id=${assignment.case_id}`;
              console.log(`[ReviewCases] Fetching answers from: ${answersUrl}`);
              const answersResponse = await api.get(answersUrl);
              console.log(`[ReviewCases] Answers response for case ${assignment.case_id}:`, answersResponse.data);
              const existingAnswers = answersResponse.data.answers || [];
              answerCount = existingAnswers.length;
              hasStarted = existingAnswers.length > 0;
              console.log(`[ReviewCases] Case ${assignment.case_id}: hasStarted=${hasStarted}, answers=${answerCount}`);
            } catch (answersError) {
              console.warn(`[ReviewCases] Warning checking answers for case ${assignment.case_id}:`, answersError.message);
              // On error, default to hasStarted = false (safe fallback)
              hasStarted = false;
              answerCount = 0;
            }
            
            const enhanced = {
              ...assignment,
              caseData: caseData,
              hasStarted,
              answerCount
            };
            console.log(`[ReviewCases] Enhanced case ${enhanced.case_id}: batch_is_expired=${enhanced.batch_is_expired}, hasStarted=${enhanced.hasStarted}`);
            return enhanced;
          } catch (error) {
            console.error(`[ReviewCases] Error enhancing assignment ${assignment.id}:`, error.message);
            return {
              ...assignment,
              caseData: { id: assignment.case_id, customerId: assignment.case_customer_id },
              hasStarted: false,
              answerCount: 0
            };
          }
        })
      );
      
      // SORT: Put partially answered cases (hasStarted=true) at the top
      const sortedCases = enhancedCases.sort((a, b) => {
        // Partial answers (hasStarted) come first
        if (a.hasStarted && !b.hasStarted) return -1;
        if (!a.hasStarted && b.hasStarted) return 1;
        // Otherwise, maintain order (newest first)
        return new Date(b.assigned_at) - new Date(a.assigned_at);
      });
      
      console.log('[ReviewCases] Sorted pending cases, first case hasStarted:', sortedCases[0]?.hasStarted);
      console.log('[ReviewCases] First 3 cases batch_is_expired status:', sortedCases.slice(0, 3).map(c => ({
        case_id: c.case_id,
        batch_is_expired: c.batch_is_expired,
        hasStarted: c.hasStarted
      })));
      setPendingCases(sortedCases);
    } catch (error) {
      console.error('Error loading pending cases:', error);
      setPendingCases([]);
    } finally {
      setPendingCasesLoading(false);
    }
  };

  // Load completed cases for selected protocol
  useEffect(() => {
    setCompletedCurrentPage(1); // Reset to page 1 when protocol changes
    if (selectedCompletedProtocol) {
      loadCompletedCases(selectedCompletedProtocol);
    } else {
      setCompletedCases([]);
    }
  }, [selectedCompletedProtocol]);

  // Function to fetch questions and answers for a completed case
  const handleViewAnswers = async (caseItem) => {
    setSelectedCaseForReview(caseItem);
    setShowAnswersModal(true);
    setCaseAnswersLoading(true);
    
    try {
      // Use cached questions instead of direct API call
      const questionsResponse = await getQuestions(caseItem.protocol_id);
      const questionsData = questionsResponse.questions || [];
      
      // Fetch answers for this specific case
      const answersResponse = await api.get(
        `/answers?protocol_id=${caseItem.protocol_id}&case_id=${caseItem.case_id}`
      );
      const answersData = answersResponse.data.answers || [];
      
      setCaseQuestions(questionsData);
      setCaseAnswers(answersData);
    } catch (error) {
      console.error('Error fetching case answers:', error);
      setCaseQuestions([]);
      setCaseAnswers([]);
    } finally {
      setCaseAnswersLoading(false);
    }
  };

  // Close the answers modal
  const closeAnswersModal = () => {
    setShowAnswersModal(false);
    setSelectedCaseForReview(null);
    setCaseQuestions([]);
    setCaseAnswers([]);
  };

  const loadCompletedCases = async (protocolId) => {
    setCompletedCasesLoading(true);
    try {
      // Get both direct assignments AND batch assignments for completed cases
      const response = await api.get(
        `/assignments?doctor_id=${user.id}&protocol_id=${protocolId}&status=completed`
      );
      let assignments = response.data.assignments || [];
      
      console.log('[ReviewCases] Loaded completed direct assignments:', assignments.length);
      
      // Also get batch assignments (only show completed ones)
      try {
        const batchesResponse = await api.get('/batches/my-batches?status=all');
        const batches = batchesResponse.data.batches || [];
        console.log('[ReviewCases] Loaded batches for completed check:', batches.length);
        
        // For each batch, get the completed cases for this protocol (if available)
        for (const batch of batches) {
          try {
            const batchCasesResponse = await api.get(
              `/batches/${batch.id}/protocol/${protocolId}/cases`
            );
            const batchCases = batchCasesResponse.data.cases || [];
            
            // Filter to only include completed batch cases (check assignment status)
            const completedBatchCases = batchCases.filter(bc => bc.status === 'completed');
            console.log(`[ReviewCases] Batch ${batch.id} has ${completedBatchCases.length} completed cases for protocol ${protocolId}`);
            
            // Convert batch cases to assignment-like objects
            const batchAssignments = completedBatchCases.map(batchCase => ({
              id: `batch_${batch.id}_case_${batchCase.case_id}`,
              doctor_id: user.id,
              protocol_id: parseInt(protocolId),
              case_id: batchCase.case_id,
              batch_id: batch.id,
              status: 'completed',
              assigned_at: batch.created_at || new Date().toISOString(),
              completed_at: batchCase.completed_at || new Date().toISOString(),
              is_batch: true
            }));
            
            assignments = assignments.concat(batchAssignments);
          } catch (batchError) {
            console.warn(`[ReviewCases] Error loading completed cases for batch ${batch.id}:`, batchError.message);
          }
        }
      } catch (batchesError) {
        console.warn('[ReviewCases] Error loading batches for completed:', batchesError.message);
      }
      
      console.log('[ReviewCases] Total completed assignments (direct + batch):', assignments.length);
      
      console.log('[ReviewCases] OPTIMIZATION: Using embedded case data from assignments endpoint');
      
      // Enhance with case information (already included via backend JOIN)
      const enhancedCases = await Promise.all(
        assignments.map(async (assignment) => {
          try {
            console.log(`[ReviewCases] Processing completed assignment ID ${assignment.id}, case ${assignment.case_id}`);
            
            // OPTIMIZATION: Use case data already embedded in assignment from backend JOIN
            const caseData = assignment.case || { id: assignment.case_id, customerId: assignment.case_customer_id };
            
            return {
              ...assignment,
              caseData: caseData
            };
          } catch (error) {
            console.error(`[ReviewCases] Error loading completed case ${assignment.case_id}:`, error.message);
            return {
              ...assignment,
              caseData: { id: assignment.case_id, customerId: assignment.case_customer_id }
            };
          }
        })
      );
      
      // Sort by most recently completed
      const sortedCases = enhancedCases.sort((a, b) => {
        return new Date(b.completed_at || b.assigned_at) - new Date(a.completed_at || a.assigned_at);
      });
      
      setCompletedCases(sortedCases);
    } catch (error) {
      console.error('Error loading completed cases:', error);
      setCompletedCases([]);
    } finally {
      setCompletedCasesLoading(false);
    }
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <ResearchLayout pageTitle="Review Cases">
        <Preloader message="Loading your assigned protocols..." />
      </ResearchLayout>
    );
  }

  return (
    <ResearchLayout pageTitle="Review Cases">
      <div className="container-fluid">
        
        {/* SECTION 1: PENDING CASES - COMMENTED OUT */}

        {/* SECTION 2: COMPLETED CASES */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-warning text-dark">
                <h5 className="mb-0">
                  <i className="fas fa-check-circle me-2"></i>
                  Completed Cases
                  <span className="badge bg-light text-dark ms-2">
                    {protocols.reduce((sum, p) => sum + p.completedCount, 0)} total
                  </span>
                </h5>
              </div>
              <div className="card-body">
                {/* Protocol Selector */}
                <div className="mb-4">
                  <label className="form-label fw-bold">
                    <i className="fas fa-clipboard-list me-2"></i>
                    Select Protocol
                  </label>
                  <select
                    className="form-select"
                    value={selectedCompletedProtocol}
                    onChange={(e) => {
                      setSelectedCompletedProtocol(e.target.value);
                      // Find the selected protocol and get its show_ai_response setting
                      const selectedProtocol = protocols.find(p => p.id === e.target.value);
                      setSelectedProtocolAiResponse(selectedProtocol?.showAiResponse || selectedProtocol?.show_ai_response || false);
                    }}
                  >
                    <option value="">-- Select a Protocol --</option>
                    {protocols
                      .filter(p => p.completedCount > 0)
                      .map((protocol) => (
                        <option key={protocol.id} value={protocol.id}>
                          {protocol.name} ({protocol.completedCount} completed)
                        </option>
                      ))}
                  </select>
                </div>

                {/* Completed Cases List with Pagination */}
                {completedCasesLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-success" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : selectedCompletedProtocol ? (
                  completedCases.length > 0 ? (
                    <>
                      {/* Pagination Info */}
                      <div className="mb-3 d-flex justify-content-between align-items-center">
                        <div className="text-muted small">
                          Showing {((completedCurrentPage - 1) * completedCasesPerPage) + 1} to{' '}
                          {Math.min(completedCurrentPage * completedCasesPerPage, completedCases.length)} of{' '}
                          {completedCases.length} cases
                        </div>
                      </div>

                      {/* Cases Grid */}
                      <div className="row">
                        {completedCases
                          .slice(
                            (completedCurrentPage - 1) * completedCasesPerPage,
                            completedCurrentPage * completedCasesPerPage
                          )
                          .map((caseItem) => (
                            <div key={caseItem.id} className="col-lg-4 col-md-6 mb-4">
                              <div className="card border border-success h-100">
                                <div className="card-header bg-light text-center position-relative">
                                  {/* Batch Badge */}
                                  {caseItem.batch_name && (
                                    <div className="position-absolute top-0 end-0 p-2">
                                      <span className="badge bg-success">
                                        {caseItem.batch_name}
                                      </span>
                                    </div>
                                  )}
                                  <h6 className="mb-0 text-warning fw-bold">
                                    <i className="fas fa-user me-2"></i>
                                    Case ID: {caseItem.case_id}
                                  </h6>
                                </div>
                                <div className="card-body">
                                  <div className="mb-3">
                                    <small className="text-muted d-block">
                                      <i className="fas fa-calendar me-1"></i>
                                      Assigned: {formatDate(caseItem.assigned_at)}
                                    </small>
                                    <small className="text-success d-block mt-1">
                                      <i className="fas fa-check me-1"></i>
                                      Completed: {formatDate(caseItem.completed_at)}
                                    </small>
                                    {(caseItem.caseData?.customerId || caseItem.caseData?.customer_id) && (
                                      <small className="text-muted d-block mt-1">
                                        <i className="fas fa-id-card me-1"></i>
                                        Customer ID: {caseItem.caseData.customerId || caseItem.caseData.customer_id}
                                      </small>
                                    )}
                                  </div>
                                  <div className="mt-auto">
                                    <button
                                      onClick={() => handleViewAnswers(caseItem)}
                                      className="btn btn-primary btn-sm w-100"
                                    >
                                      <i className="fas fa-eye me-1"></i>
                                      View Answers
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>

                      {/* Pagination Controls for Completed Cases */}
                      {Math.ceil(completedCases.length / completedCasesPerPage) > 1 && (
                        <nav aria-label="Completed cases pagination" className="mt-4">
                          <ul className="pagination justify-content-center">
                            <li className={`page-item ${completedCurrentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => setCompletedCurrentPage(completedCurrentPage - 1)}
                                disabled={completedCurrentPage === 1}
                              >
                                <i className="fas fa-chevron-left me-1"></i>
                                Previous
                              </button>
                            </li>
                            {Array.from({ length: Math.ceil(completedCases.length / completedCasesPerPage) }).map((_, i) => (
                              <li key={i + 1} className={`page-item ${completedCurrentPage === i + 1 ? 'active' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => setCompletedCurrentPage(i + 1)}
                                >
                                  {i + 1}
                                </button>
                              </li>
                            ))}
                            <li className={`page-item ${completedCurrentPage === Math.ceil(completedCases.length / completedCasesPerPage) ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => setCompletedCurrentPage(completedCurrentPage + 1)}
                                disabled={completedCurrentPage === Math.ceil(completedCases.length / completedCasesPerPage)}
                              >
                                Next
                                <i className="fas fa-chevron-right ms-1"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <i className="fas fa-folder-open fa-3x text-muted mb-3"></i>
                      <p className="text-muted">No completed cases for this protocol.</p>
                    </div>
                  )
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
                    <p className="text-muted">Select a protocol to view completed cases.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modal for viewing answers */}
        <Modal
          isOpen={showAnswersModal}
          onClose={closeAnswersModal}
          title={`Case Review - Case ID: ${selectedCaseForReview?.case_id || 'N/A'}`}
          size="xl"
        >
          {caseAnswersLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading answers...</p>
            </div>
          ) : caseQuestions.length > 0 ? (
            <div className="row g-0">
              {/* Left Side - Case Details */}
              <div className="col-lg-5">
                <div className="p-3" style={{ overflowY: 'auto', maxHeight: '65vh' }}>
                  <h6 className="text-success mb-3">
                    <i className="fas fa-user-injured me-2"></i>
                    Case Details
                  </h6>
                  <CaseDetailsCard caseId={selectedCaseForReview?.case_id} squeezed={true} showAiResponse={selectedProtocolAiResponse} />
                </div>
              </div>

              {/* Right Side - Questions & Answers */}
              <div className="col-lg-7 border-start">
                <div className="p-3" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                  <h6 className="text-primary mb-3">
                    <i className="fas fa-question-circle me-2"></i>
                    Questions & Answers ({caseQuestions.length})
                  </h6>

                  <div className="answers-list">
                    {caseQuestions.map((question, index) => {
                      const answer = caseAnswers.find(a => a.question_id === question.id);
                      return (
                        <div key={question.id} className="mb-3">
                          <div className="card">
                            <div className="card-header bg-light">
                              <h6 className="mb-0 text-primary">
                                <i className="fas fa-question-circle me-2"></i>
                                Q{index + 1}: {question.question_text || question.text || 'N/A'}
                              </h6>
                              {question.type && (
                                <span className="badge bg-info ms-2">{question.type}</span>
                              )}
                            </div>
                            <div className="card-body">
                              <p className="card-text mb-0">
                                <strong>A{index + 1}: </strong>
                                {answer ? (
                                  <span className="text-success">
                                    {answer.content || answer.answer_text || answer.answer || 'N/A'}
                                  </span>
                                ) : (
                                  <span className="text-muted fst-italic">No answer</span>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <i className="fas fa-exclamation-circle fa-3x text-muted mb-3"></i>
              <p className="text-muted">No questions or answers found for this case.</p>
            </div>
          )}
        </Modal>

      {/* Access Denial Modal */}
      <Modal
        isOpen={showAccessDenialModal}
        title={accessDenialTitle}
        onClose={() => setShowAccessDenialModal(false)}
      >
        <div className="text-center">
          {accessDenialMessage.split('\n').map((line, index) => (
            <p key={index} className="mb-2">{line}</p>
          ))}
        </div>
      </Modal>

      </div>
    </ResearchLayout>
  );
};

export default ReviewCases;
